import { INSERTED_TRAILING_PARAGRAPH_TO_LAST_NODE_META } from '@atlaskit/editor-common/block-type';
import { expandToBlockRange, isMultiBlockRange } from '@atlaskit/editor-common/selection';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { ResolvedPos } from '@atlaskit/editor-prosemirror/model';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import type {
	NodeSelection,
	ReadonlyTransaction,
	Selection,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';
import type { CellSelection } from '@atlaskit/editor-tables';
import { getTableSelectionClosesToPos } from '@atlaskit/editor-tables/utils';

import type { BlockControlsPlugin } from '../../blockControlsPluginType';
import { getBlockControlsMeta, key } from '../main';
import { newGetSelection } from './getSelection';

export const getMultiSelectionIfPosInside = (
	api: ExtractInjectionAPI<BlockControlsPlugin>,
	pos: number,
	tr?: Transaction,
): { anchor?: number; head?: number } => {
	const pluginState = api?.blockControls?.sharedState.currentState();
	// With move nodes shortcut, we expand selection and move node within one transaction,
	// Hence we also look for `multiSelectDnD` in transaction meta
	const multiSelectDnD = pluginState?.multiSelectDnD ?? tr?.getMeta(key)?.multiSelectDnD;

	if (multiSelectDnD && multiSelectDnD.anchor >= 0 && multiSelectDnD.head >= 0) {
		const multiFrom = Math.min(multiSelectDnD.anchor, multiSelectDnD.head);
		const multiTo = Math.max(multiSelectDnD.anchor, multiSelectDnD.head);

		// We subtract one as the handle position is before the node
		return pos >= multiFrom - 1 && pos < multiTo
			? { anchor: multiSelectDnD.anchor, head: multiSelectDnD.head }
			: {};
	}
	return {};
};

/**
 * Given a handle position, returns the from and to positions of the selected content.
 * If the handle position is not in a multi-selection, it returns the node's from and to positions.
 *
 * @param handlePos The position of the handle
 * @param tr The transaction to use for position calculations
 * @param api The BlockControlsPlugin API for accessing shared state
 * @returns from and to positions of the selected content (after expansion)
 */
export const getSelectedSlicePosition = (
	handlePos: number,
	tr: Transaction,
	api: ExtractInjectionAPI<BlockControlsPlugin>,
): {
	from: number;
	to: number;
} => {
	const { anchor, head } = getMultiSelectionIfPosInside(api, handlePos, tr);
	const inSelection = anchor !== undefined && head !== undefined;
	const from = inSelection ? Math.min(anchor || 0, head || 0) : handlePos;

	const activeNode = tr.doc.nodeAt(handlePos);
	const activeNodeEndPos = handlePos + (activeNode?.nodeSize ?? 1);
	const to = inSelection ? Math.max(anchor || 0, head || 0) : activeNodeEndPos;

	return { from, to };
};

/**
 * This maps a preserved selection through a transaction, expanding text selections to block boundaries.
 *
 * @param selection The existing preserved selection to map
 * @param tr The transaction to map through
 * @returns The mapped selection or undefined if mapping is not possible
 */
export const mapPreservedSelection = (
	selection: Selection,
	tr: ReadonlyTransaction | Transaction,
): Selection | undefined => {
	const { preservedSelectionMapping } = getBlockControlsMeta(tr) || {};

	const mapping = preservedSelectionMapping || tr.mapping;

	const from = mapping.map(selection.from);
	// When lastNodeMustBeParagraph inserts a trailing paragraph exactly at the end boundary
	// of a preserved block selection, keep the mapped end position on the left side of the
	// inserted paragraph so it is not included in the preserved selection.
	const shouldTrimTrailingParagraph =
		tr.getMeta(INSERTED_TRAILING_PARAGRAPH_TO_LAST_NODE_META) === true;
	const to = shouldTrimTrailingParagraph
		? mapping.map(selection.to, -1)
		: mapping.map(selection.to);

	const isSelectionEmpty = from === to;
	const wasSelectionEmpty = selection.from === selection.to;

	if (isSelectionEmpty && !wasSelectionEmpty) {
		// If selection has become empty i.e. content has been deleted, stop preserving
		return undefined;
	}

	return createPreservedSelection(tr.doc.resolve(from), tr.doc.resolve(to));
};

const isInsideEmptyTextblock = ($pos: ResolvedPos) =>
	$pos.parent.isTextblock && $pos.parent.content.size === 0;

const isAtEndOfParent = ($pos: ResolvedPos) => $pos.parentOffset === $pos.parent.content.size;

const isAtStartOfParent = ($pos: ResolvedPos) => $pos.parentOffset === 0;

/**
 * Adjust selection bounds to exclude nodes where the selection only touches
 * the edge position without selecting any content.
 *
 * Exception: Don't adjust if the selection is inside an empty textblock,
 * as we want to include empty paragraphs in block operations.
 *
 * @param $from The resolved position of the start of the selection
 * @param $to The resolved position of the end of the selection
 * @returns Adjusted $from and $to positions
 */
export const adjustSelectionBoundsForEdgePositions = (
	$from: ResolvedPos,
	$to: ResolvedPos,
): { $from: ResolvedPos; $to: ResolvedPos } => {
	const { doc } = $from;

	// Walk $from forward while at end of ancestors
	let adjustedFrom = $from;
	if (!isInsideEmptyTextblock($from)) {
		while (adjustedFrom.depth > 0 && isAtEndOfParent(adjustedFrom)) {
			const nextPos = adjustedFrom.after();
			if (nextPos > doc.content.size || nextPos === adjustedFrom.pos) {
				break;
			}
			adjustedFrom = doc.resolve(nextPos);
		}
	}

	// Walk $to backward while at start of ancestors
	let adjustedTo = $to;
	if (!isInsideEmptyTextblock($to)) {
		while (adjustedTo.depth > 0 && isAtStartOfParent(adjustedTo)) {
			const prevPos = adjustedTo.before();
			if (prevPos < 0 || prevPos === adjustedTo.pos) {
				break;
			}
			adjustedTo = doc.resolve(prevPos);
		}
	}

	return { $from: adjustedFrom, $to: adjustedTo };
};
/**
 * Creates a preserved selection which is expanded to block boundaries.
 *
 * Will return the correct type of selection based on the nodes contained within the
 * expanded selection range.
 *
 * If the selection becomes empty or invalid, it returns undefined.
 *
 * @param $from The resolved position of the start of the selection
 * @param $to The resolved position of the end of the selection
 * @returns A Selection or undefined if selection is invalid
 */
export const createPreservedSelection = (
	$from: ResolvedPos,
	$to: ResolvedPos,
): TextSelection | CellSelection | NodeSelection | undefined => {
	const { doc } = $from;

	const isCollapsed = $from.pos === $to.pos;
	const adjusted = isCollapsed ? { $from, $to } : adjustSelectionBoundsForEdgePositions($from, $to);
	if (!isCollapsed && adjusted.$from.pos >= adjusted.$to.pos) {
		return undefined;
	}

	// expand the selection range to block boundaries, so selection always includes whole nodes
	const expanded = expandToBlockRange(adjusted.$from, adjusted.$to);

	// stop preserving if selection becomes invalid
	if (
		expanded.$from.pos < 0 ||
		expanded.$to.pos > doc.content.size ||
		expanded.$from.pos >= expanded.$to.pos
	) {
		return undefined;
	}

	// If multiple blocks selected, create TextSelection from start of first node to end of last node
	if (expanded.range && isMultiBlockRange(expanded.range)) {
		return new TextSelection(expanded.$from, expanded.$to);
	}

	const nodeType = doc.nodeAt(expanded.$from.pos)?.type?.name;
	if (!nodeType) {
		return undefined;
	}

	if (nodeType === 'table') {
		return getTableSelectionClosesToPos($from);
	}

	return newGetSelection(doc, false, expanded.$from.pos) || undefined;
};
